import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import useSEO from "@/lib/useSEO";

export default function P2PFeature() {
  useSEO({
    title: "P2P Transfers",
    description: "Move funds between y ℶα members instantly. Search by email, send with a note. No fees between members.",
    path: "/atelier/p2p-transfers",
  });
  return (
    <main data-testid="atelier-p2p" className="bg-bg text-white min-h-screen">
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-24 pb-24">
        <Link to="/" data-testid="atelier-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-fg hover:text-gold transition-colors">
          <ArrowLeft size={14} /> Back to Atelier
        </Link>

        <div className="mt-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-6">— 01 / Atelier · P2P Transfers</div>
            <h1 className="font-display text-5xl lg:text-7xl tracking-tighter font-light leading-[0.95]">
              Money,<br/><span className="italic gold-text">passed in silence.</span>
            </h1>
            <p className="mt-8 text-secondary-fg text-lg leading-relaxed max-w-xl font-light">
              Send to anyone in the y ℶα network by email. Settlement is instant — no rails, no waits, no intermediaries. A note travels with every transfer; a private ledger keeps the record.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/register" data-testid="atelier-cta-register" className="btn-gold inline-flex items-center gap-2">Open an account <ArrowUpRight size={16} /></Link>
              <Link to="/login" className="btn-ghost">Sign in to send</Link>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5">
            <div className="glass p-8 rounded-md">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-fg mb-3">Sample transfer</div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-elevated border hairline-strong flex items-center justify-center font-display text-gold">A</div>
                <div className="text-muted-fg">→</div>
                <div className="w-12 h-12 rounded-full bg-elevated border hairline-strong flex items-center justify-center font-display text-gold">B</div>
              </div>
              <hr className="my-6 border-white/10"/>
              <div className="flex justify-between text-sm py-2"><span className="text-muted-fg">To</span><span>patron@valiant.io</span></div>
              <div className="flex justify-between text-sm py-2"><span className="text-muted-fg">Amount</span><span className="font-mono">€ 480.00</span></div>
              <div className="flex justify-between text-sm py-2"><span className="text-muted-fg">Note</span><span className="italic">For the atelier dinner.</span></div>
              <div className="flex justify-between text-sm py-2"><span className="text-muted-fg">Settles</span><span className="text-gold">● Instantly</span></div>
            </div>
          </div>
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border hairline-strong">
          {[
            ["Send by email", "Look up any y ℶα member by their address. No account numbers, no SWIFT codes."],
            ["Instant settlement", "Funds move within the network in seconds — even across currencies."],
            ["Zero member fees", "No surcharge between y ℶα members. Ever."],
            ["Private notes", "Attach a discreet message — only your counterparty sees it."],
            ["Ledger built-in", "Every transfer appears in both ledgers, signed and timestamped."],
            ["Multi-currency", "Send in any currency you hold. Recipient receives in theirs."],
          ].map(([t, b]) => (
            <div key={t} className="bg-bg p-8">
              <div className="flex items-start gap-3">
                <Check className="text-gold mt-1 shrink-0" size={18} strokeWidth={1.5} />
                <div>
                  <h3 className="font-display text-xl mb-2">{t}</h3>
                  <p className="text-secondary-fg text-sm leading-relaxed font-light">{b}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-24 text-center">
          <h2 className="font-display text-4xl lg:text-5xl tracking-tight font-light">
            Three taps. <span className="italic gold-text">Done.</span>
          </h2>
          <div className="mt-8 flex justify-center gap-4">
            <Link to="/register" className="btn-gold inline-flex items-center gap-2">Join the network <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>
    </main>
  );
}
